import type { SupportedLocale } from "@repo/core";

/** Locales available in BudgetOS (Latin-script, launch-supported). */
export const LAUNCH_LOCALES: readonly SupportedLocale[] = [
  "en",
  "es",
  "fr",
  "de",
  "pt",
  "it",
] as const;

export function isLaunchLocale(locale: string): locale is SupportedLocale {
  return (LAUNCH_LOCALES as readonly string[]).includes(locale);
}

/**
 * Etsy ships one zip per language. When `VITE_DEFAULT_LOCALE` is set at build
 * time (required for `build:release`), the UI language is fixed — no picker.
 */
export function getBuildLocale(): SupportedLocale | null {
  const raw = (import.meta.env.VITE_DEFAULT_LOCALE as string | undefined)?.trim();
  if (raw && isLaunchLocale(raw)) return raw;
  return null;
}

/** True when UI language is fixed (Etsy zip, hosted demo via ?lang=, or local ?demo=). */
export function isLocaleLocked(): boolean {
  if (import.meta.env.VITE_DEMO_MODE === "true") return true;
  if (typeof window !== "undefined" && new URLSearchParams(window.location.search).has("demo")) {
    return true;
  }
  return getBuildLocale() !== null;
}

/** Hosted demo: ?lang=es|fr|… (default en). Ignored on release/customer builds. */
export function readDemoLangParam(): SupportedLocale {
  if (typeof window === "undefined") return "en";
  const raw = new URLSearchParams(window.location.search).get("lang")?.trim().toLowerCase();
  if (raw && isLaunchLocale(raw)) return raw;
  return "en";
}

export function isHostedDemoBuild(): boolean {
  return import.meta.env.VITE_DEMO_MODE === "true";
}

/** BCP-47 tags used for Intl currency / date / calendar formatting. */
export const UI_LOCALE_TAGS: Record<SupportedLocale, string> = {
  en: "en-US",
  es: "es-ES",
  fr: "fr-FR",
  de: "de-DE",
  pt: "pt-BR",
  it: "it-IT",
};

export function uiLocaleTag(locale: SupportedLocale | string): string {
  if (isLaunchLocale(locale)) {
    return UI_LOCALE_TAGS[locale];
  }
  // Already a BCP-47 tag (e.g. en-US from older callers)
  if (locale.includes("-")) return locale;
  return "en-US";
}
